"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NextLink from "next/link";
import { useTranslation } from "react-i18next";

const ZaynProcessStepsSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const steps = t("zaynProcessSteps.items", { returnObjects: true }) || [];

  const getCardStyles = (variant) => {
    switch (variant) {
      case "highlight":
        return { bgcolor: "#7A2BF5", textColor: "#FFFFFF", labelColor: "#E6D9FF" };
      case "dark":
        return { bgcolor: "#14122B", textColor: "#FFFFFF", labelColor: "#9B93B8" };
      default:
        return { bgcolor: "#FFFFFF", textColor: "#14122B", labelColor: "#9B93B8" };
    }
  };

  return (
    <Box
      sx={{
        py: { xs: 5, sm: 6, md: 8 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Container maxWidth="lg">
        <Typography
          sx={{
            fontSize: { xs: "11px", sm: "12px" },
            fontWeight: 600,
            color: "#A020E8",
            textTransform: "uppercase",
            letterSpacing: "1px",
            fontFamily: "var(--font-outfit)",
            textAlign: "center",
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          {t("zaynProcessSteps.label")}
        </Typography>

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "22px", sm: "26px", md: "30px" },
            fontWeight: 700,
            lineHeight: 1.3,
            color: "#14122B",
            fontFamily: "var(--font-plusjakarta)",
            textAlign: "center",
            mb: { xs: 2, sm: 2.5, md: 3 },
          }}
        >
          {t("zaynProcessSteps.title")}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: "13px", sm: "14px" },
            color: "#55556E",
            lineHeight: 1.8,
            fontWeight: 400,
            fontFamily: "var(--font-outfit)",
            textAlign: "center",
            maxWidth: 620,
            mx: "auto",
            mb: { xs: 4, sm: 5, md: 6 },
          }}
        >
          {t("zaynProcessSteps.description")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            gap: { xs: 2, sm: 2.5, md: 0 },
            mb: { xs: 4, sm: 5 },
          }}
        >
          {steps.map((step, index) => {
            const styles = getCardStyles(step.variant);
            const isLast = index === steps.length - 1;
            return (
              <React.Fragment key={index}>
                <Box
                  sx={{
                    bgcolor: styles.bgcolor,
                    border: step.variant === "default" ? "1px solid #EDEDF2" : "none",
                    borderRadius: "14px",
                    p: { xs: 2.2, sm: 2.5 },
                    textAlign: isArabic ? "right" : "left",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {step.stepLabel && (
                    <Typography
                      sx={{
                        fontSize: { xs: "10px", sm: "10.5px" },
                        fontWeight: 700,
                        color: styles.labelColor,
                        textTransform: "uppercase",
                        letterSpacing: "1.5px",
                        fontFamily: "var(--font-outfit)",
                        mb: { xs: 1, sm: 1.2 },
                      }}
                    >
                      {step.stepLabel}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      fontSize: { xs: "14px", sm: "15px" },
                      fontWeight: 700,
                      color: styles.textColor,
                      fontFamily: "var(--font-plusjakarta)",
                      lineHeight: 1.4,
                    }}
                  >
                    {step.text}
                  </Typography>
                </Box>

                {!isLast && (
                  <Box
                    sx={{
                      display: { xs: "none", md: "flex" },
                      alignItems: "center",
                      justifyContent: "center",
                      width: { md: 32, lg: 40 },
                      flexShrink: 0,
                    }}
                  >
                    <ArrowForwardIcon
                      sx={{
                        color: "#D9B3EF",
                        fontSize: { md: 22, lg: 22 },
                        transform: isArabic ? "rotate(180deg)" : "none",
                      }}
                    />
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center" ,mt: { xs: 1, sm: 6 }}}>
          <NextLink
            href="/free-trial-of-ai-recruiting-tool"
            passHref
            legacyBehavior
          >
            <Button
              component="a"
              sx={{
                bgcolor: "#000",
                color: "#fff",
                px: { xs: 3.5, sm: 4 },
                py: { xs: 1.2, sm: 1.4 },
                borderRadius: "50px",
                textTransform: "none",
                fontSize: { xs: "14px", sm: "15px" },
                fontWeight: 500,
                fontFamily: "var(--font-outfit)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: "#7A2BF5",
                  transform: "scale(1.03)",
                },
              }}
            >
              {t("zaynProcessSteps.cta")}
            </Button>
          </NextLink>
        </Box>
      </Container>
    </Box>
  );
};

export default ZaynProcessStepsSection;